/**
 * StoreSwapToggle — one-tap "swap store" pill for admins/DMs who cover
 * sister stores.
 *
 * Problem it solves (user report 2026-02): "I covered a store yesterday,
 * and let me tell you the inconvenience of having to switch back and
 * forth". Prior to this, once an admin exited Store Mirror to peek at
 * their home store, re-entering mirror on the covered store meant:
 * Profile → Admin → Stores → find store → tap Mirror. Four+ taps every
 * time they wanted to swap.
 *
 * Behavior:
 *   • Only renders for admins/DMs (`user.is_admin` or
 *     `user.is_district_manager`).
 *   • Reads/writes `wowLastCoveredStore:{crew_id}` in localStorage —
 *     `{ id, code, name, city, savedAt }`. Entries older than 24h are
 *     treated as expired so the pill doesn't linger for weeks.
 *   • When the actor is on their home store AND a fresh last-covered
 *     store exists, the pill shows "🏪 Cover {code}" — one tap
 *     re-enters mirror on that store.
 *   • When the actor is currently mirroring, the pill hides itself —
 *     the mirror banner's own "Return" button already handles going
 *     home in one tap, no duplicate UI.
 *   • Long-press / secondary tap on the pill dismisses it for the day
 *     (persisted to `wowLastCoveredStoreHidden:{crew_id}:{date}`) so
 *     admins who don't want the reminder can silence it without
 *     losing the memory.
 */
import { useEffect, useState } from "react";
import { X } from "@phosphor-icons/react";

const STORAGE_KEY = "wowLastCoveredStore";
const DISMISS_KEY = "wowLastCoveredStoreHidden";
const FRESH_MS = 24 * 60 * 60 * 1000; // 24h — stale entries don't surface

// Read helpers kept out of the component so callers (App.js) can save
// entries at mirror-enter time and inspect state without duplicating
// the schema.
export function saveLastCoveredStore(crewId, store) {
  if (!crewId || !store?.id) return;
  try {
    const payload = {
      id: store.id,
      code: store.code || "",
      name: store.name || "",
      city: store.city || "",
      savedAt: Date.now(),
    };
    localStorage.setItem(`${STORAGE_KEY}:${crewId}`, JSON.stringify(payload));
  } catch { /* quota */ }
}

function readLastCoveredStore(crewId) {
  if (!crewId) return null;
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}:${crewId}`);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (!p?.id || !p?.savedAt) return null;
    if (Date.now() - p.savedAt > FRESH_MS) return null;
    return p;
  } catch { return null; }
}

function isDismissedToday(crewId) {
  if (!crewId) return false;
  try {
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
    return localStorage.getItem(`${DISMISS_KEY}:${crewId}:${today}`) === "1";
  } catch { return false; }
}

function markDismissedToday(crewId) {
  if (!crewId) return;
  try {
    const today = new Date().toLocaleDateString("en-CA", { timeZone: "America/Denver" });
    localStorage.setItem(`${DISMISS_KEY}:${crewId}:${today}`, "1");
  } catch { /* quota */ }
}

export default function StoreSwapToggle({ user, viewingStoreId, onSwapTo }) {
  const [tick, setTick] = useState(0);          // forces re-read after tap
  const [dismissed, setDismissed] = useState(false);
  const isAdminish = !!(user?.is_admin || user?.is_district_manager);
  const crewId = user?.crew_id || "";

  useEffect(() => {
    setDismissed(isDismissedToday(crewId));
  }, [crewId, tick]);

  // Re-read on scope-change events so the pill hides immediately when
  // the admin enters mirror mode (App.js dispatches this on every
  // setViewingStoreId change).
  useEffect(() => {
    const onScope = () => setTick((t) => t + 1);
    window.addEventListener("wow:scope-changed", onScope);
    return () => window.removeEventListener("wow:scope-changed", onScope);
  }, []);

  if (!isAdminish) return null;
  if (viewingStoreId) return null;               // already mirroring — banner handles it
  if (dismissed) return null;
  const last = readLastCoveredStore(crewId);
  if (!last) return null;
  if (last.id === user?.home_store_id) return null; // covered store IS home — no swap needed

  return (
    <div
      className="fixed top-2 right-2 z-[65] flex items-center gap-1"
      data-testid="store-swap-toggle-wrap"
    >
      <button
        type="button"
        onClick={() => onSwapTo && onSwapTo(last)}
        className="flex items-center gap-1.5 h-8 px-3 rounded-full bg-emerald-500 hover:bg-emerald-600 active:scale-95 text-white text-[11px] font-black uppercase tracking-wider shadow-lg transition-all"
        title={`Jump back to ${last.code} ${last.name}`}
        data-testid="store-swap-toggle"
      >
        <span aria-hidden>🔄</span>
        <span className="whitespace-nowrap">Cover {last.code}</span>
      </button>
      <button
        type="button"
        onClick={() => { markDismissedToday(crewId); setDismissed(true); }}
        className="w-6 h-6 rounded-full bg-slate-900/60 hover:bg-slate-900/80 text-white flex items-center justify-center shadow-md active:scale-90 transition-all"
        aria-label="Hide swap store shortcut"
        title="Hide for today"
        data-testid="store-swap-toggle-dismiss"
      >
        <X weight="bold" className="w-3 h-3" />
      </button>
    </div>
  );
}
